import type {
  ProductFieldAccess,
  ProductFieldPermissionRecord,
  ProductFieldPermissionTarget,
} from './ProductFieldPermission.types'

type ResolveProductFieldAccessOptions = ProductFieldPermissionTarget & {
  companyId?: string
  hasManualSource?: boolean
  permissions: ProductFieldPermissionRecord[]
  roleCode?: string
}

const PRODUCT_MODULE = 'products'
const MANUAL_SOURCE = 'manual'
const SUPPLIER_FIELD = 'supplier'

function normalize(value?: string | null) {
  return value?.trim().toLowerCase() ?? ''
}

export function getProductFieldPermissionSource({
  hasManualSource,
  isEditMode,
  productSource,
}: {
  hasManualSource?: boolean
  isEditMode: boolean
  productSource?: string | null
}) {
  if (isEditMode) return productSource

  return hasManualSource ? MANUAL_SOURCE : ''
}

function getAccessPriority(access: ProductFieldAccess) {
  if (access === 'hidden') return 2
  if (access === 'read') return 1

  return 0
}

function matchesTarget(
  permission: ProductFieldPermissionRecord,
  target: ProductFieldPermissionTarget
) {
  const matchesProperty =
    Boolean(target.propertyId) && permission.property_id === target.propertyId
  const matchesField =
    Boolean(target.field) &&
    normalize(permission.field) === normalize(target.field)

  return matchesProperty || matchesField
}

export function resolveProductFieldAccess({
  companyId,
  field,
  hasManualSource,
  isProductCreatedBySupplier,
  isSupplierUser,
  permissions,
  productSource,
  propertyId,
  roleCode,
}: ResolveProductFieldAccessOptions): ProductFieldAccess {
  if (hasManualSource && normalize(productSource) === MANUAL_SOURCE) {
    return 'write'
  }

  // Supplier keeps its existing product-specific locking rules, but otherwise
  // follows configured field permissions.
  if (
    normalize(field) === SUPPLIER_FIELD &&
    (isSupplierUser || isProductCreatedBySupplier)
  ) {
    return 'write'
  }

  const activeProductPermissions = permissions.filter(
    (permission) =>
      !permission.deletedAt && normalize(permission.module) === PRODUCT_MODULE
  )
  const defaultAccess: ProductFieldAccess = activeProductPermissions.length
    ? 'read'
    : 'write'
  const matchingPermissions = activeProductPermissions.filter((permission) => {
    const permissionRole = normalize(permission.role_code)
    const matchesRole =
      permissionRole === '*' || permissionRole === normalize(roleCode)

    return matchesRole && matchesTarget(permission, { field, propertyId })
  })
  const companyPermissions = companyId
    ? matchingPermissions.filter(
        (permission) => permission.company_id === companyId
      )
    : []
  const fallbackPermissions = matchingPermissions.filter(
    (permission) => permission.company_id === null
  )
  const effectivePermissions = companyPermissions.length
    ? companyPermissions
    : fallbackPermissions

  if (!effectivePermissions.length) return defaultAccess

  return effectivePermissions.reduce<ProductFieldAccess>(
    (effectiveAccess, permission) =>
      getAccessPriority(permission.access) > getAccessPriority(effectiveAccess)
        ? permission.access
        : effectiveAccess,
    'write'
  )
}
